/**
 * @file TransferWaiterModal.tsx
 * @description Dialog allowing servers to transfer table responsibility to another team member.
 */

import React, { useState } from 'react';
import { X, Users } from 'lucide-react';
import type { WaiterTable } from '../../../types/waiter.types';

interface TransferWaiterModalProps {
  isOpen: boolean;
  onClose: () => void;
  table: WaiterTable;
  onConfirmTransfer: (newWaiterId: string, newWaiterName: string) => Promise<void>;
}

// Available waitstaff team for handoffs
const TEAM_MEMBERS = [
  { id: 'usr-imad', name: 'Imad' },
  { id: 'usr-sara', name: 'Sara' },
  { id: 'usr-youssef', name: 'Youssef' },
  { id: 'usr-ahmed', name: 'Ahmed' },
];

export const TransferWaiterModal: React.FC<TransferWaiterModalProps> = ({
  isOpen,
  onClose,
  table,
  onConfirmTransfer,
}) => {
  const [selectedStaff, setSelectedStaff] = useState<typeof TEAM_MEMBERS[0] | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  if (!isOpen) return null;

  const handleTransfer = async () => {
    if (!selectedStaff) return;
    try {
      setIsSubmitting(true);
      await onConfirmTransfer(selectedStaff.id, selectedStaff.name);
      onClose();
    } catch (err) {
      console.error('Transfer error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
      <div className="w-full max-w-md bg-zinc-950 border border-zinc-800 rounded-t-3xl sm:rounded-3xl p-6 shadow-2xl flex flex-col max-h-[85dvh] pb-[calc(1.5rem+env(safe-area-inset-bottom))] sm:pb-6">
        {/* Mobile slide-up drag handle */}
        <div className="sm:hidden w-full flex justify-center -mt-2 pb-3 shrink-0">
          <div className="w-10 h-1 rounded-full bg-zinc-700" />
        </div>

        <div className="flex items-center justify-between mb-4 shrink-0">
          <div className="flex items-center gap-2 text-indigo-400">
            <Users className="w-5 h-5 shrink-0" />
            <h3 className="text-base font-black uppercase text-white">Transfer Table</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-xl text-zinc-400 hover:text-white hover:bg-zinc-900 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-xs text-zinc-400 mb-4 shrink-0">
          Reassign <span className="font-bold text-white">TABLE {table.table_number}</span> to another floor server.
          The entire order will follow.
        </p>

        <div className="space-y-2 mb-6 overflow-y-auto flex-1 pr-1">
          {TEAM_MEMBERS.map(member => (
            <button
              key={member.id}
              type="button"
              onClick={() => setSelectedStaff(member)}
              className={`w-full p-3.5 min-h-[48px] rounded-xl text-xs font-bold text-left flex items-center justify-between border transition-all active:scale-[0.98] ${
                selectedStaff?.id === member.id
                  ? 'bg-indigo-500/10 text-indigo-300 border-indigo-500'
                  : 'bg-zinc-900 text-zinc-300 border-zinc-800'
              }`}
            >
              <span>{member.name}</span>
              <span className="text-[11px] text-zinc-500 font-normal">Active On Shift</span>
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3.5 min-h-[44px] rounded-xl bg-zinc-900 text-zinc-400 text-xs font-bold hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!selectedStaff || isSubmitting}
            onClick={handleTransfer}
            className="flex-1 py-3.5 min-h-[44px] rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-black uppercase tracking-wider disabled:opacity-50 active:scale-[0.98] transition-all"
          >
            {isSubmitting ? 'Transferring...' : 'Transfer'}
          </button>
        </div>
      </div>
    </div>
  );
};
